import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { PedidoItem, Aviso, Moneda, Usuario, ProductoVariante } from '../types';
import { useToast } from './ToastContext';

interface CartItem extends PedidoItem {
    id_tienda: number;
    moneda: Moneda;
}

interface CartContextType {
    items: CartItem[];
    total: number;
    storeId: number | null;
    addToCart: (aviso: Aviso, quantity?: number, variant?: ProductoVariante) => boolean;
    removeFromCart: (id_aviso: number, id_variante?: number) => void;
    updateQuantity: (id_aviso: number, quantity: number, id_variante?: number) => void;
    clearCart: () => void;
    itemCount: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode; currentUser: Usuario | null }> = ({ children, currentUser }) => {
    const [items, setItems] = useState<CartItem[]>([]);
    const [storeId, setStoreId] = useState<number | null>(null);
    const [initialized, setInitialized] = useState(false);
    const { showToast } = useToast();

    const getStorageKey = () => {
        if (currentUser) {
            return `shopping_cart_${currentUser.id_usuario}_v2`;
        }
        return 'shopping_cart_guest_v2';
    };

    // Load cart from localStorage when currentUser changes (or on mount)
    useEffect(() => {
        setInitialized(false);
        const key = getStorageKey();
        const savedCart = localStorage.getItem(key);
        if (savedCart) {
            try {
                const parsedCart = JSON.parse(savedCart);
                setItems(parsedCart.items || []);
                setStoreId(parsedCart.storeId || null);
            } catch (e) {
                console.error("Error parsing cart from localStorage", e);
                setItems([]);
                setStoreId(null);
            }
        } else {
            // New user or no saved cart for this user -> reset state
            setItems([]);
            setStoreId(null);
        }
        setInitialized(true);
    }, [currentUser]); // Re-run when user changes

    // Save cart to localStorage whenever it changes
    // Save cart to localStorage whenever it changes
    useEffect(() => {
        if (!initialized) return;

        const key = getStorageKey();
        localStorage.setItem(key, JSON.stringify({ items, storeId }));
    }, [items, storeId, currentUser, initialized]); // Include currentUser in dependency to save to correct key

    const addToCart = (aviso: Aviso, quantity: number = 1, variant?: ProductoVariante): boolean => {
        // Warning: if aviso has no id_tienda, we assume it's not a store item (or use id_usuario as fallback if legacy?)
        // Ideally we should block non-store items if the system requires a FK to tiendas.
        const targetStoreId = aviso.id_tienda;

        if (!targetStoreId) {
            console.warn("Attempted to add item without store ID to cart");
            // Optional: Allow if system supports non-store sales, but our error says FK required.
            // For now, return false or show error.
            showToast('Este producto no pertenece a una tienda habilitada para venta online.', 'error');
            return false;
        }

        // Check if item belongs to the same store
        if (storeId !== null && storeId !== targetStoreId) {
            // We can't use confirm/alert here easily without blocking or UI. 
            // The component calling this should handle the confirmation if this returns false.
            // But for now, we'll return false to indicate failure due to store mismatch.
            return false;
        }

        const effectivePrice = variant?.precio ?? aviso.precio;

        setItems(prevItems => {
            // Find item matching both ID and Variant
            const existingItem = prevItems.find(item =>
                item.id_aviso === aviso.id_aviso &&
                (item.id_variante === (variant?.id_variante || undefined))
            );

            if (existingItem) {
                return prevItems.map(item =>
                    (item.id_aviso === aviso.id_aviso && item.id_variante === (variant?.id_variante || undefined))
                        ? { ...item, cantidad: item.cantidad + quantity, subtotal: (item.cantidad + quantity) * item.precio }
                        : item
                );
            } else {
                return [...prevItems, {
                    id_aviso: aviso.id_aviso,
                    titulo: aviso.titulo,
                    precio: effectivePrice,
                    cantidad: quantity,
                    subtotal: effectivePrice * quantity,
                    imagen: aviso.imagenes?.[0] || '',
                    id_tienda: targetStoreId, // Use the real Store ID
                    moneda: aviso.moneda || Moneda.ARS,
                    id_variante: variant?.id_variante,
                    nombre_variante: variant?.nombre
                }];
            }
        });

        if (storeId === null) {
            setStoreId(targetStoreId);
        }

        showToast('Producto agregado al carrito', 'success');
        return true;
    };

    const removeFromCart = (id_aviso: number, id_variante?: number) => {
        setItems(prevItems => {
            const newItems = prevItems.filter(item =>
                !(item.id_aviso === id_aviso && item.id_variante === id_variante)
            );
            if (newItems.length === 0) {
                setStoreId(null);
            }
            return newItems;
        });
    };

    const updateQuantity = (id_aviso: number, quantity: number, id_variante?: number) => {
        if (quantity <= 0) {
            removeFromCart(id_aviso, id_variante);
            return;
        }
        setItems(prevItems => prevItems.map(item =>
            (item.id_aviso === id_aviso && item.id_variante === id_variante)
                ? { ...item, cantidad: quantity, subtotal: quantity * item.precio }
                : item
        ));
    };

    const clearCart = () => {
        setItems([]);
        setStoreId(null);
    };

    const total = items.reduce((sum, item) => sum + item.subtotal, 0);
    const itemCount = items.reduce((sum, item) => sum + item.cantidad, 0);

    return (
        <CartContext.Provider value={{ items, total, storeId, addToCart, removeFromCart, updateQuantity, clearCart, itemCount }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (context === undefined) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};
